import type {
  BookedResponse,
  ReserveHoldRequest,
  FailedKoalendarDeliveries,
  RetryKoalendarDeliveriesResponse,
  ReserveConfirmResponse,
  ReserveOptionsResponse,
  ReserveRequest,
  ReserveResponse,
  SharedRoomResponse,
  ShareLinkResponse,
  VisitCardResponse,
  AddStaffMemberRequest,
  AppointmentDetailResponse,
  AppointmentFavoritesResponse,
  GownPickerCatalog,
  GownPickerResponse,
  SaveFavoritesRequest,
  AppointmentLinkResponse,
  AppointmentTypesResponse,
  AssignAppointmentRequest,
  CreateAppointmentRequest,
  CreateAppointmentResponse,
  FollowUpQueueResponse,
  RescheduleAppointmentRequest,
  SetAppointmentStatusRequest,
  SetFollowUpRequest,
  SetShopifyOrderNoRequest,
  UpdateAppointmentRequest,
  ApiError,
  ApplySyncRequest,
  AssigneesResponse,
  BackOfficeSettings,
  AuthConfigResponse,
  AvailabilityResponse,
  BookRequest,
  BookingSettings,
  UpdateBookingSettings,
  CatalogueListResponse,
  CommandsRequest,
  CommandsResponse,
  CompletenessResponse,
  EnquiryListResponse,
  GarmentResponse,
  GoogleSignInRequest,
  LinkEnquiryRequest,
  LinkEnquiryResponse,
  PullSheetResponse,
  RackItemResponse,
  RackListResponse,
  RankedGarmentsResponse,
  RecordOutcomesRequest,
  SessionView,
  SetGarmentStatusRequest,
  SetRackItemStatusRequest,
  StaffIdentity,
  StaffSessionResponse,
  PublishStorefrontRequest,
  StartSessionResponse,
  StorefrontPlan,
  StorefrontRunResponse,
  SyncRunResponse,
  ThemeHistoryResponse,
  ThemeWorkspaceGrant,
  ThemeWorkspaceStatus,
  SyncScan,
  AddUpstreamOptionRequest,
  RemoveUpstreamWordRequest,
  SetUpstreamWordRequest,
  UpstreamVocabularyResponse,
  StaffMember,
  TeamResponse,
  UpdateBackOfficeSettings,
  UpdateStaffMemberRequest,
  VisitListResponse,
  CreateOrderRequest,
  EmailInvoiceRequest,
  EmailInvoiceResponse,
  InvoiceEmailStatusResponse,
  GarmentLookupResponse,
  LinkOrderAppointmentRequest,
  OrderDetailResponse,
  SupplierItemsResponse,
  SupplierRefreshResponse,
  OrderLineInput,
  OrderListResponse,
  RackLookupResponse,
  SetSalesStaffRequest,
  UpdateOrderLineRequest,
  UpdateOrderRequest,
  PaymentsReportResponse,
  RecordPaymentRequest,
  ShopifyRecentOrdersResponse,
  AdvanceFulfillmentRequest,
  ArrivalNoticeEmailRequest,
  ArrivalNoticeEmailResponse,
  ArrivalNoticePreviewRequest,
  ArrivalNoticePreviewResponse,
  RecordArrivalNoticeRequest,
  AlertsResponse,
  MarketingVisitsResponse,
  PickupOverrideRequest,
  UpdateLineTrackingRequest,
  CalculateCommissionResponse,
  CommissionPayoutResponse,
  CommissionResponse,
  DiscardPayoutResponse,
} from '@fitting-room/contracts'

/**
 * Where the API is.
 *
 * Empty by default, which means "this origin" — the shape the dev server (Vite
 * proxies `/api` and `/fixtures`) and any reverse-proxied deployment both have.
 * A deployment that serves the client from a DIFFERENT origin to the API sets
 * `VITE_API_BASE_URL` at build time, and every request and every asset path in
 * this app goes through the two functions below, so that is the only place the
 * split shows up.
 */
export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')

/**
 * An asset the API serves, made absolute.
 *
 * Media arrives as whatever the catalogue stored: an absolute URL for anything
 * in object storage, and a root-relative `/fixtures/...` path for the demo
 * catalogue the API serves itself. Only the second kind needs the base — and a
 * client that prepended it to the first would turn every real photograph into
 * a 404.
 */
export function assetUrl(url: string): string {
  if (!url || !API_BASE) return url
  return /^[a-z]+:\/\//i.test(url) || url.startsWith('//') ? url : `${API_BASE}${url}`
}

/**
 * Every URL the client knows about lives in this file and nowhere else.
 *
 * The response *shapes* are the contract's and are never restated here; only
 * the paths are local knowledge. If the API moves a route, exactly one file
 * changes, and if it changes a payload the typecheck fails at the call site.
 */
export const routes = {
  session: (token: string) => `/api/s/${encodeURIComponent(token)}`,
  sessionGarments: (token: string) => `/api/s/${encodeURIComponent(token)}/garments`,
  sessionGarmentsByIds: (token: string) => `/api/s/${encodeURIComponent(token)}/garments/by-ids`,
  sessionCommands: (token: string) => `/api/s/${encodeURIComponent(token)}/commands`,
  sessionRefill: (token: string) => `/api/s/${encodeURIComponent(token)}/refill`,
  sessionAvailability: (token: string) => `/api/s/${encodeURIComponent(token)}/availability`,
  sessionBook: (token: string) => `/api/s/${encodeURIComponent(token)}/book`,
  adminSettings: () => `/api/admin/settings`,
  adminTeam: () => `/api/admin/team`,
  adminTeamMember: (id: string) => `/api/admin/team/${encodeURIComponent(id)}`,
  adminTeamMemberActive: (id: string, active: boolean) =>
    `/api/admin/team/${encodeURIComponent(id)}/${active ? 'reactivate' : 'deactivate'}`,
  adminAssignees: () => `/api/admin/assignees`,
  adminBackOffice: () => `/api/admin/back-office`,
  adminKoalendarFailed: () => `/api/admin/koalendar/failed`,
  adminKoalendarRetry: () => `/api/admin/koalendar/failed/retry`,
  adminVisits: () => `/api/admin/visits`,
  adminVisit: (visitId: string) => `/api/admin/visits/${encodeURIComponent(visitId)}`,
  adminVisitAction: (
    visitId: string,
    action: 'reschedule' | 'assign' | 'status' | 'link' | 'favorites' | 'follow-up' | 'follow-up/done' | 'follow-up/clear',
  ) =>
    `/api/admin/visits/${encodeURIComponent(visitId)}/${action}`,
  adminFollowUps: () => `/api/admin/follow-ups`,
  adminOrders: () => `/api/admin/orders`,
  adminOrder: (orderId: string) => `/api/admin/orders/${encodeURIComponent(orderId)}`,
  adminOrderAction: (orderId: string, action: 'sales-staff' | 'lines' | 'cancel' | 'restore' | 'appointments') =>
    `/api/admin/orders/${encodeURIComponent(orderId)}/${action}`,
  adminOrderLine: (orderId: string, lineId: string) =>
    `/api/admin/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(lineId)}`,
  adminOrderUnlink: (orderId: string, visitId: string) =>
    `/api/admin/orders/${encodeURIComponent(orderId)}/appointments/${encodeURIComponent(visitId)}/unlink`,
  adminOrderPayments: (orderId: string) => `/api/admin/orders/${encodeURIComponent(orderId)}/payments`,
  adminOrderAccounting: (orderId: string) => `/api/admin/orders/${encodeURIComponent(orderId)}/accounting-checked`,
  adminOrderInvoicePdf: (orderId: string) => `/api/admin/orders/${encodeURIComponent(orderId)}/invoice.pdf`,
  adminOrderInvoiceEmail: (orderId: string) => `/api/admin/orders/${encodeURIComponent(orderId)}/invoice/email`,
  adminInvoiceEmailStatus: () => `/api/admin/invoice-email`,
  adminVoidPayment: (paymentId: string) => `/api/admin/payments/${encodeURIComponent(paymentId)}/void`,
  adminPaymentShopifyOrderNo: (paymentId: string) => `/api/admin/payments/${encodeURIComponent(paymentId)}/shopify-order-no`,
  adminPaymentsReport: () => `/api/admin/payments/report`,
  adminShopifyRecentOrders: () => `/api/admin/shopify/orders/recent`,
  adminLineAction: (orderId: string, lineId: string, action:
      | 'fulfillment'
      | 'fulfillment/undo'
      | 'tracking'
      | 'pickup-override'
      | 'arrival-notice/email'
      | 'arrival-notice/preview'
      | 'arrival-notice/record'
      | 'arrival-notice/undo',
  ) =>
    `/api/admin/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(lineId)}/${action}`,
  adminAlerts: () => `/api/admin/alerts`,
  adminCommission: () => `/api/admin/commission`,
  adminCommissionCalculate: () => `/api/admin/commission/calculate`,
  adminPayout: (payoutId: string) => `/api/admin/commission/payouts/${encodeURIComponent(payoutId)}`,
  adminPayoutAction: (payoutId: string, action: 'confirm' | 'discard') =>
    `/api/admin/commission/payouts/${encodeURIComponent(payoutId)}/${action}`,
  adminGarmentLookup: () => `/api/admin/order-lookup/garments`,
  adminRackLookup: () => `/api/admin/order-lookup/rack`,
  adminAppointmentTypes: () => `/api/admin/appointment-types`,
  adminGowns: () => `/api/admin/gowns`,
  adminPullSheet: (visitId: string) => `/api/admin/visits/${encodeURIComponent(visitId)}/pull-sheet`,
  adminOutcomes: (visitId: string) => `/api/admin/visits/${encodeURIComponent(visitId)}/outcomes`,
  adminCompleteness: () => `/api/admin/completeness`,
  adminEnquiries: () => `/api/admin/enquiries`,
  adminLinkEnquiry: (sessionId: string) => `/api/admin/enquiries/${encodeURIComponent(sessionId)}/link`,
  adminCatalogue: () => `/api/admin/catalogue`,
  adminGarmentStatus: (garmentId: string) => `/api/admin/catalogue/${encodeURIComponent(garmentId)}/status`,
  adminRack: () => `/api/admin/rack`,
  adminRackItemStatus: (itemId: string) => `/api/admin/rack/${encodeURIComponent(itemId)}/status`,
  adminSyncScan: () => `/api/admin/sync/gowns/scan`,
  adminSyncApply: () => `/api/admin/sync/gowns/apply`,
  adminSyncRun: () => `/api/admin/sync/gowns/run`,
  adminSyncWords: () => `/api/admin/sync/words`,
  adminSyncWordsNewOption: () => `/api/admin/sync/words/new-option`,
  adminSyncWordsRemove: () => `/api/admin/sync/words/remove`,
  adminStorefrontPlan: () => `/api/admin/storefront/plan`,
  adminStorefrontPublish: () => `/api/admin/storefront/publish`,
  adminStorefrontRun: () => `/api/admin/storefront/run`,
  adminThemeWorkspace: () => `/api/admin/theme/workspace`,
  adminThemeHistory: (path?: string) => `/api/admin/theme/history${path ? `?path=${encodeURIComponent(path)}` : ''}`,
  demoLinks: () => `/api/demo/links`,
  start: () => `/api/start`,
  reserve: () => `/api/reserve`,
  reserveConfirm: (id: string) => `/api/reserve/${encodeURIComponent(id)}/confirm`,
  reserveHold: () => `/api/reserve/hold`,
  booked: (bookingId?: string) => (bookingId ? `/api/booked/${encodeURIComponent(bookingId)}` : '/api/booked'),
  sessionShare: (token: string) => `/api/s/${encodeURIComponent(token)}/share`,
  shared: (shareToken: string) => `/api/v/${encodeURIComponent(shareToken)}`,
  adminVisitCard: (visitId: string) => `/api/admin/visits/${encodeURIComponent(visitId)}/card`,
  adminVisitNoShowFee: (visitId: string) => `/api/admin/visits/${encodeURIComponent(visitId)}/no-show-fee`,
  authConfig: () => `/api/auth/config`,
  authGoogle: () => `/api/auth/google`,
  authMe: () => `/api/auth/me`,
  adminMarketingVisits: (from: string, to: string) =>
    `/api/admin/marketing/visits?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
} as const

export class ApiRequestError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: Record<string, unknown> | undefined

  constructor(status: number, code: string, message: string, detail?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.code = code
    this.detail = detail
  }
}

function isApiError(value: unknown): value is ApiError {
  if (typeof value !== 'object' || value === null) return false
  const candidate = (value as { error?: unknown }).error
  if (typeof candidate !== 'object' || candidate === null) return false
  return typeof (candidate as { code?: unknown }).code === 'string'
}

/** The staff credential travels as `Authorization: Bearer <token>`. */
export const staffAuthHeader = (token: string): Record<string, string> => ({ authorization: `Bearer ${token}` })

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | undefined>
  staffToken?: string | null
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, staffToken, signal } = options

  let url = `${API_BASE}${path}`
  if (query) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === '') continue
      params.set(key, String(value))
    }
    const serialized = params.toString()
    if (serialized) url = `${API_BASE}${path}?${serialized}`
  }

  const headers: Record<string, string> = { accept: 'application/json' }
  if (body !== undefined) headers['content-type'] = 'application/json'
  if (staffToken) Object.assign(headers, staffAuthHeader(staffToken))

  const init: RequestInit = { method, headers, credentials: 'same-origin' }
  if (body !== undefined) init.body = JSON.stringify(body)
  if (signal) init.signal = signal

  const response = await fetch(url, init)
  const raw = await response.text()
  let parsed: unknown = undefined
  if (raw) {
    try {
      parsed = JSON.parse(raw) as unknown
    } catch {
      parsed = undefined
    }
  }

  if (!response.ok) {
    if (isApiError(parsed)) {
      throw new ApiRequestError(response.status, parsed.error.code, parsed.error.message, parsed.error.detail)
    }
    throw new ApiRequestError(response.status, 'http_error', raw.slice(0, 400) || response.statusText)
  }

  return parsed as T
}

/* ------------------------------- customer -------------------------------- */

export function fetchSession(token: string, signal?: AbortSignal): Promise<SessionView> {
  return request<SessionView>(routes.session(token), { ...(signal ? { signal } : {}) })
}

/** The deepest page the ranked endpoint will serve. */
export const GARMENT_PAGE_MAX = 200

export interface GarmentQueryParams {
  limit?: number
  offset?: number
}

export function fetchRankedGarments(
  token: string,
  params: GarmentQueryParams = {},
  signal?: AbortSignal,
): Promise<RankedGarmentsResponse> {
  return request<RankedGarmentsResponse>(routes.sessionGarments(token), {
    query: { ...params },
    ...(signal ? { signal } : {}),
  })
}

/**
 * Specific garments, by id.
 *
 * The shortlist and the summary draw PICKS, and a pick is not a rank: it can
 * sit outside any page of the deck, and it can belong to the catalogue she is
 * not currently browsing. Matching picks against a deep page of the ranked
 * deck therefore failed in both cases, and the screens printed the raw
 * internal id in place of the gown.
 */
export function fetchGarmentsByIds(
  token: string,
  ids: readonly string[],
  signal?: AbortSignal,
): Promise<RankedGarmentsResponse> {
  return request<RankedGarmentsResponse>(routes.sessionGarmentsByIds(token), {
    query: { ids: [...ids].join(',') },
    ...(signal ? { signal } : {}),
  })
}

export function postCommands(token: string, body: CommandsRequest): Promise<CommandsResponse> {
  return request<CommandsResponse>(routes.sessionCommands(token), { method: 'POST', body })
}

/**
 * Fill it in again: a second session for the same appointment, and the link to
 * it. Authenticated by the token she is already holding, which is why it sits
 * under `/api/s/:token` rather than beside the public door.
 *
 * The same `{ token, url }` the public door returns — one shape for "here is a
 * session, here is how to open it" — and a second tap returns the refill she
 * already started rather than a third session.
 */
export function startRefill(token: string): Promise<StartSessionResponse> {
  return request<StartSessionResponse>(routes.sessionRefill(token), { method: 'POST' })
}

/** The times her line's appointment type can be booked at, by the boutique's calendar day. */
export function fetchAvailability(token: string, signal?: AbortSignal): Promise<AvailabilityResponse> {
  return request<AvailabilityResponse>(routes.sessionAvailability(token), { ...(signal ? { signal } : {}) })
}

export function bookAppointment(token: string, body: BookRequest): Promise<{ visitId: string; scheduledAt: string }> {
  return request(routes.sessionBook(token), { method: 'POST', body })
}

/* --------------------------------- staff --------------------------------- */

/** Opening hours and the size chart — the owner's Settings page. */
export function fetchSettings(staffToken: string, signal?: AbortSignal): Promise<BookingSettings> {
  return request<BookingSettings>(routes.adminSettings(), { staffToken, ...(signal ? { signal } : {}) })
}

export function saveSettings(staffToken: string, body: UpdateBookingSettings): Promise<BookingSettings> {
  return request<BookingSettings>(routes.adminSettings(), { method: 'PUT', staffToken, body })
}

/** Settings → Team, and the deployment directory beside it. Owner only. */
export function fetchTeam(staffToken: string, signal?: AbortSignal): Promise<TeamResponse> {
  return request<TeamResponse>(routes.adminTeam(), { staffToken, ...(signal ? { signal } : {}) })
}

export function addTeamMember(staffToken: string, body: AddStaffMemberRequest): Promise<StaffMember> {
  return request<StaffMember>(routes.adminTeam(), { method: 'POST', staffToken, body })
}

export function updateTeamMember(staffToken: string, id: string, body: UpdateStaffMemberRequest): Promise<StaffMember> {
  return request<StaffMember>(routes.adminTeamMember(id), { method: 'PUT', staffToken, body })
}

export function setTeamMemberActive(staffToken: string, id: string, active: boolean): Promise<StaffMember> {
  return request<StaffMember>(routes.adminTeamMemberActive(id, active), { method: 'POST', staffToken, body: {} })
}

/** Who an appointment or order can be put against. Readable by any stylist. */
export function fetchAssignees(staffToken: string, signal?: AbortSignal): Promise<AssigneesResponse> {
  return request<AssigneesResponse>(routes.adminAssignees(), { staffToken, ...(signal ? { signal } : {}) })
}

/** The back office's settings: read by anyone on the team, written by the owner. */
export function fetchBackOffice(staffToken: string, signal?: AbortSignal): Promise<BackOfficeSettings> {
  return request<BackOfficeSettings>(routes.adminBackOffice(), { staffToken, ...(signal ? { signal } : {}) })
}

export function saveBackOffice(staffToken: string, body: UpdateBackOfficeSettings): Promise<BackOfficeSettings> {
  return request<BackOfficeSettings>(routes.adminBackOffice(), { method: 'PUT', staffToken, body })
}

/** Koalendar deliveries that couldn't be acted on, the last 30 days. Owner only. */
export function fetchFailedKoalendar(staffToken: string, signal?: AbortSignal): Promise<FailedKoalendarDeliveries> {
  return request<FailedKoalendarDeliveries>(routes.adminKoalendarFailed(), { staffToken, ...(signal ? { signal } : {}) })
}

export function retryFailedKoalendar(staffToken: string): Promise<RetryKoalendarDeliveriesResponse> {
  return request<RetryKoalendarDeliveriesResponse>(routes.adminKoalendarRetry(), { method: 'POST', staffToken })
}

/**
 * One day, or a span of them — the day sheet and the month grid are the same
 * list read over different ends, and the API answers both from one route.
 */
export function fetchVisits(
  staffToken: string,
  window: { readonly date: string } | { readonly from: string; readonly to: string },
  signal?: AbortSignal,
  filters: AppointmentFilters = {},
): Promise<VisitListResponse> {
  return request<VisitListResponse>(routes.adminVisits(), {
    query: {
      ...('date' in window ? { date: window.date } : { from: window.from, to: window.to }),
      status: filters.statuses?.join(','),
      assignee: filters.assignees?.join(','),
      source: filters.sources?.join(','),
      q: filters.q?.trim(),
    },
    staffToken,
    ...(signal ? { signal } : {}),
  })
}

/** The list's server-side filters. Within one, values are OR; across them, AND. */
export interface AppointmentFilters {
  readonly statuses?: readonly string[]
  /** Team member ids, and `none` for nobody yet. */
  readonly assignees?: readonly string[]
  readonly sources?: readonly string[]
  readonly q?: string
}

/* ------------------------------ appointments ----------------------------- */

export function fetchAppointmentTypes(staffToken: string, signal?: AbortSignal): Promise<AppointmentTypesResponse> {
  return request<AppointmentTypesResponse>(routes.adminAppointmentTypes(), { staffToken, ...(signal ? { signal } : {}) })
}

export function fetchAppointment(staffToken: string, visitId: string, signal?: AbortSignal): Promise<AppointmentDetailResponse> {
  return request<AppointmentDetailResponse>(routes.adminVisit(visitId), { staffToken, ...(signal ? { signal } : {}) })
}

export function fetchAppointmentFavorites(staffToken: string, visitId: string, signal?: AbortSignal): Promise<AppointmentFavoritesResponse> {
  return request<AppointmentFavoritesResponse>(routes.adminVisitAction(visitId, 'favorites'), {
    staffToken,
    ...(signal ? { signal } : {}),
  })
}

/** The favorites picker's Save: what the stylist added and took off, applied all or nothing. */
export function saveAppointmentFavorites(staffToken: string, visitId: string, body: SaveFavoritesRequest): Promise<AppointmentFavoritesResponse> {
  return request<AppointmentFavoritesResponse>(routes.adminVisitAction(visitId, 'favorites'), { method: 'PUT', staffToken, body })
}

/** One page of the favorites picker's catalog. */
export function browseGowns(
  staffToken: string,
  params: { catalog: GownPickerCatalog; q: string; page: number },
  signal?: AbortSignal,
): Promise<GownPickerResponse> {
  return request<GownPickerResponse>(routes.adminGowns(), {
    staffToken,
    query: { catalog: params.catalog, q: params.q, page: params.page },
    ...(signal ? { signal } : {}),
  })
}

export function createAppointment(staffToken: string, body: CreateAppointmentRequest): Promise<CreateAppointmentResponse> {
  return request<CreateAppointmentResponse>(routes.adminVisits(), { method: 'POST', staffToken, body })
}

export function updateAppointment(staffToken: string, visitId: string, body: UpdateAppointmentRequest): Promise<AppointmentDetailResponse> {
  return request<AppointmentDetailResponse>(routes.adminVisit(visitId), { method: 'PUT', staffToken, body })
}

export function rescheduleAppointment(
  staffToken: string,
  visitId: string,
  body: RescheduleAppointmentRequest,
): Promise<AppointmentDetailResponse> {
  return request<AppointmentDetailResponse>(routes.adminVisitAction(visitId, 'reschedule'), { method: 'POST', staffToken, body })
}

export function assignAppointment(staffToken: string, visitId: string, body: AssignAppointmentRequest): Promise<AppointmentDetailResponse> {
  return request<AppointmentDetailResponse>(routes.adminVisitAction(visitId, 'assign'), { method: 'POST', staffToken, body })
}

export function setAppointmentStatus(
  staffToken: string,
  visitId: string,
  body: SetAppointmentStatusRequest,
): Promise<AppointmentDetailResponse> {
  return request<AppointmentDetailResponse>(routes.adminVisitAction(visitId, 'status'), { method: 'POST', staffToken, body })
}

export function setFollowUp(staffToken: string, visitId: string, body: SetFollowUpRequest): Promise<AppointmentDetailResponse> {
  return request<AppointmentDetailResponse>(routes.adminVisitAction(visitId, 'follow-up'), { method: 'PUT', staffToken, body })
}

export function closeFollowUp(staffToken: string, visitId: string, how: 'done' | 'clear'): Promise<AppointmentDetailResponse> {
  return request<AppointmentDetailResponse>(routes.adminVisitAction(visitId, how === 'done' ? 'follow-up/done' : 'follow-up/clear'), {
    method: 'POST',
    staffToken,
    body: {},
  })
}

/** A new link to the appointment's fitting room. Minted per request: none is stored readable. */
export function mintAppointmentLink(staffToken: string, visitId: string): Promise<AppointmentLinkResponse> {
  return request<AppointmentLinkResponse>(routes.adminVisitAction(visitId, 'link'), { method: 'POST', staffToken, body: {} })
}

export function fetchFollowUps(staffToken: string, signal?: AbortSignal): Promise<FollowUpQueueResponse> {
  return request<FollowUpQueueResponse>(routes.adminFollowUps(), { staffToken, ...(signal ? { signal } : {}) })
}

/* --------------------------------- orders -------------------------------- */

/**
 * The list's filters: statuses, kinds and bridal stylists are OR'd within
 * themselves, the "where it stands" flags AND'd; the search is the number,
 * name, email, phone or Shopify order number.
 */
export interface OrderFilters {
  readonly statuses?: readonly string[]
  readonly flags?: readonly string[]
  readonly kinds?: readonly string[]
  /** Team member ids, and `none` for no bridal stylist. */
  readonly stylists?: readonly string[]
  readonly q?: string
  readonly limit?: number
  readonly offset?: number
}

export function fetchOrders(staffToken: string, filters: OrderFilters = {}, signal?: AbortSignal): Promise<OrderListResponse> {
  return request<OrderListResponse>(routes.adminOrders(), {
    query: {
      status: filters.statuses?.join(','),
      show: filters.flags?.join(','),
      kind: filters.kinds?.join(','),
      stylist: filters.stylists?.join(','),
      q: filters.q?.trim(),
      limit: filters.limit,
      offset: filters.offset,
    },
    staffToken,
    ...(signal ? { signal } : {}),
  })
}

export function fetchOrder(staffToken: string, orderId: string, signal?: AbortSignal): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrder(orderId), { staffToken, ...(signal ? { signal } : {}) })
}

export function createOrder(staffToken: string, body: CreateOrderRequest): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrders(), { method: 'POST', staffToken, body })
}

export function updateOrder(staffToken: string, orderId: string, body: UpdateOrderRequest): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrder(orderId), { method: 'PUT', staffToken, body })
}

export function setOrderSalesStaff(staffToken: string, orderId: string, body: SetSalesStaffRequest): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrderAction(orderId, 'sales-staff'), { method: 'POST', staffToken, body })
}

export function addOrderLine(staffToken: string, orderId: string, body: OrderLineInput): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrderAction(orderId, 'lines'), { method: 'POST', staffToken, body })
}

export function updateOrderLine(
  staffToken: string,
  orderId: string,
  lineId: string,
  body: UpdateOrderLineRequest,
): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrderLine(orderId, lineId), { method: 'PUT', staffToken, body })
}

/** The Jovita page: every OMS item, what it is linked to or likely belongs to. */
export function fetchSupplierItems(staffToken: string, signal?: AbortSignal): Promise<SupplierItemsResponse> {
  return request<SupplierItemsResponse>('/api/admin/supplier/items', { staffToken, ...(signal ? { signal } : {}) })
}

/** Catch linked lines up with the OMS; a no-op within ten minutes of the last. */
export function refreshSupplierLines(staffToken: string): Promise<SupplierRefreshResponse> {
  return request<SupplierRefreshResponse>('/api/admin/supplier/refresh', { method: 'POST', staffToken })
}

export function linkSupplierItem(staffToken: string, orderId: string, lineId: string, itemId: string): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(`${routes.adminOrderLine(orderId, lineId)}/supplier-link`, { method: 'POST', staffToken, body: { itemId } })
}

export function unlinkSupplierItem(staffToken: string, orderId: string, lineId: string): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(`${routes.adminOrderLine(orderId, lineId)}/supplier-link`, { method: 'DELETE', staffToken })
}

export function removeOrderLine(staffToken: string, orderId: string, lineId: string, reason: string): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(`${routes.adminOrderLine(orderId, lineId)}/remove`, { method: 'POST', staffToken, body: { reason } })
}

export function setOrderStatus(staffToken: string, orderId: string, to: 'cancel' | 'restore', reason: string): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrderAction(orderId, to), { method: 'POST', staffToken, body: { reason } })
}

export function linkOrderAppointment(staffToken: string, orderId: string, body: LinkOrderAppointmentRequest): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrderAction(orderId, 'appointments'), { method: 'POST', staffToken, body })
}

export function unlinkOrderAppointment(staffToken: string, orderId: string, visitId: string): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrderUnlink(orderId, visitId), { method: 'POST', staffToken, body: {} })
}

/* --------------------------------- invoice -------------------------------- */

/**
 * The invoice PDF, as bytes. Fetched rather than linked: the console's
 * credential travels in a header, which a plain link cannot carry.
 */
export async function fetchInvoicePdf(staffToken: string, orderId: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(`${API_BASE}${routes.adminOrderInvoicePdf(orderId)}`, {
    headers: { accept: 'application/pdf', ...staffAuthHeader(staffToken) },
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  })
  if (!response.ok) {
    const raw = await response.text()
    let parsed: unknown
    try {
      parsed = JSON.parse(raw) as unknown
    } catch {
      parsed = undefined
    }
    if (isApiError(parsed)) throw new ApiRequestError(response.status, parsed.error.code, parsed.error.message, parsed.error.detail)
    throw new ApiRequestError(response.status, 'http_error', raw.slice(0, 400) || response.statusText)
  }
  return response.blob()
}

/** Emails the invoice, the PDF attached. `sent: false` with `configured: false` means there is no mail server yet. */
export function emailInvoice(staffToken: string, orderId: string, body: EmailInvoiceRequest): Promise<EmailInvoiceResponse> {
  return request<EmailInvoiceResponse>(routes.adminOrderInvoiceEmail(orderId), { method: 'POST', staffToken, body })
}

export function fetchInvoiceEmailStatus(staffToken: string, signal?: AbortSignal): Promise<InvoiceEmailStatusResponse> {
  return request<InvoiceEmailStatusResponse>(routes.adminInvoiceEmailStatus(), { staffToken, ...(signal ? { signal } : {}) })
}

/* -------------------------------- payments ------------------------------- */

/** Records a payment on an order. Answers with the whole order, ledger and balance included. */
export function recordPayment(staffToken: string, orderId: string, body: RecordPaymentRequest): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrderPayments(orderId), { method: 'POST', staffToken, body })
}

/** Voids a payment, with a reason. Answers with the order it was on. */
export function voidPayment(staffToken: string, paymentId: string, reason: string): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminVoidPayment(paymentId), { method: 'POST', staffToken, body: { reason } })
}

/**
 * Type, correct or clear the Shopify order number on a payment that stands
 * (`null` or `''` clears it). The only edit a payment takes; the money never changes.
 */
export function setPaymentShopifyOrderNo(staffToken: string, paymentId: string, shopifyOrderNo: string | null): Promise<OrderDetailResponse> {
  const body: SetShopifyOrderNoRequest = { shopifyOrderNo }
  return request<OrderDetailResponse>(routes.adminPaymentShopifyOrderNo(paymentId), { method: 'PUT', staffToken, body })
}

/** The owner's "accounting checked" flag. */
export function setAccountingChecked(staffToken: string, orderId: string, checked: boolean): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminOrderAccounting(orderId), { method: 'POST', staffToken, body: { checked } })
}

export function fetchPaymentsReport(
  staffToken: string,
  range: { readonly from: string; readonly to: string },
  signal?: AbortSignal,
): Promise<PaymentsReportResponse> {
  return request<PaymentsReportResponse>(routes.adminPaymentsReport(), { query: { ...range }, staffToken, ...(signal ? { signal } : {}) })
}

export interface ShopifyLookup {
  readonly hours: number
  /** Cents. Orders whose total or one of whose charges is exactly this. */
  readonly amountMinor?: number | undefined
  /** How many to read, newest first: 25 when absent, at most 100. */
  readonly limit?: number | undefined
}

export function fetchShopifyRecentOrders(staffToken: string, lookup: ShopifyLookup, signal?: AbortSignal): Promise<ShopifyRecentOrdersResponse> {
  return request<ShopifyRecentOrdersResponse>(routes.adminShopifyRecentOrders(), {
    query: { hours: lookup.hours, amount: lookup.amountMinor, limit: lookup.limit },
    staffToken,
    ...(signal ? { signal } : {}),
  })
}

/* ------------------------------ pickup & arrival ------------------------------ */

/** Moves a line on one step. Answers with the whole order, its status and alerts included. */
export function advanceOrderLine(staffToken: string, orderId: string, lineId: string, body: AdvanceFulfillmentRequest): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminLineAction(orderId, lineId, 'fulfillment'), { method: 'POST', staffToken, body })
}

/** One step back, with a reason. */
export function undoOrderLine(staffToken: string, orderId: string, lineId: string, reason: string): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminLineAction(orderId, lineId, 'fulfillment/undo'), { method: 'POST', staffToken, body: { reason } })
}

/** The supplier's number, the order day, the ETA, held-until. */
export function updateLineTracking(staffToken: string, orderId: string, lineId: string, body: UpdateLineTrackingRequest): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminLineAction(orderId, lineId, 'tracking'), { method: 'PUT', staffToken, body })
}

/** Emails the customer that a line has arrived. `configured: false` when nothing could be sent. */
export function emailArrivalNotice(staffToken: string, orderId: string, lineId: string, body: ArrivalNoticeEmailRequest): Promise<ArrivalNoticeEmailResponse> {
  return request<ArrivalNoticeEmailResponse>(routes.adminLineAction(orderId, lineId, 'arrival-notice/email'), { method: 'POST', staffToken, body })
}

/** The arrival email as it would go. Sends and writes nothing. */
export function previewArrivalNotice(staffToken: string, orderId: string, lineId: string, body: ArrivalNoticePreviewRequest): Promise<ArrivalNoticePreviewResponse> {
  return request<ArrivalNoticePreviewResponse>(routes.adminLineAction(orderId, lineId, 'arrival-notice/preview'), { method: 'POST', staffToken, body })
}

/** Told some other way: a call, a text, at the counter. */
export function recordArrivalNotice(staffToken: string, orderId: string, lineId: string, body: RecordArrivalNoticeRequest): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminLineAction(orderId, lineId, 'arrival-notice/record'), { method: 'POST', staffToken, body })
}

/** Takes the mark back. */
export function undoArrivalNotice(staffToken: string, orderId: string, lineId: string): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminLineAction(orderId, lineId, 'arrival-notice/undo'), { method: 'POST', staffToken })
}

/** The owner's: it goes home with a balance due. */
export function overridePickup(staffToken: string, orderId: string, lineId: string, body: PickupOverrideRequest): Promise<OrderDetailResponse> {
  return request<OrderDetailResponse>(routes.adminLineAction(orderId, lineId, 'pickup-override'), { method: 'POST', staffToken, body })
}

export function fetchAlerts(staffToken: string, signal?: AbortSignal): Promise<AlertsResponse> {
  return request<AlertsResponse>(routes.adminAlerts(), { staffToken, ...(signal ? { signal } : {}) })
}

/** The marketing partner's one read: the appointments in a range of days, the customer already masked by the server. */
export function fetchMarketingVisits(staffToken: string, from: string, to: string, signal?: AbortSignal): Promise<MarketingVisitsResponse> {
  return request<MarketingVisitsResponse>(routes.adminMarketingVisits(from, to), { staffToken, ...(signal ? { signal } : {}) })
}

/* -------------------------------- commission ------------------------------- */

/** The owner's commission screen: pending payouts, what can be calculated, and a page of the history. */
export function fetchCommission(
  staffToken: string,
  page: { readonly limit: number; readonly offset: number },
  signal?: AbortSignal,
): Promise<CommissionResponse> {
  return request<CommissionResponse>(routes.adminCommission(), { query: { ...page }, staffToken, ...(signal ? { signal } : {}) })
}

/** One pending payout per person among these orders. */
export function calculateCommission(staffToken: string, orderIds: readonly string[]): Promise<CalculateCommissionResponse> {
  return request<CalculateCommissionResponse>(routes.adminCommissionCalculate(), { method: 'POST', staffToken, body: { orderIds } })
}

export function updatePayoutNote(staffToken: string, payoutId: string, note: string): Promise<CommissionPayoutResponse> {
  return request<CommissionPayoutResponse>(routes.adminPayout(payoutId), { method: 'PUT', staffToken, body: { note } })
}

/** The money has gone; the note says how. Final from here. */
export function confirmPayout(staffToken: string, payoutId: string, note: string): Promise<CommissionPayoutResponse> {
  return request<CommissionPayoutResponse>(routes.adminPayoutAction(payoutId, 'confirm'), { method: 'POST', staffToken, body: { note } })
}

export function discardPayout(staffToken: string, payoutId: string): Promise<DiscardPayoutResponse> {
  return request<DiscardPayoutResponse>(routes.adminPayoutAction(payoutId, 'discard'), { method: 'POST', staffToken, body: {} })
}

export function lookupGarments(staffToken: string, q: string, signal?: AbortSignal): Promise<GarmentLookupResponse> {
  return request<GarmentLookupResponse>(routes.adminGarmentLookup(), { query: { q }, staffToken, ...(signal ? { signal } : {}) })
}

export function lookupRack(staffToken: string, q: string, signal?: AbortSignal): Promise<RackLookupResponse> {
  return request<RackLookupResponse>(routes.adminRackLookup(), { query: { q }, staffToken, ...(signal ? { signal } : {}) })
}

/**
 * `sessionId` asks for one particular version of this appointment's list,
 * read-only. Omitted — the ordinary case — the API answers with the
 * authoritative one: the newest she has sent.
 */
export function fetchPullSheet(
  staffToken: string,
  visitId: string,
  sessionId?: string,
  signal?: AbortSignal,
): Promise<PullSheetResponse> {
  return request<PullSheetResponse>(routes.adminPullSheet(visitId), {
    ...(sessionId ? { query: { session: sessionId } } : {}),
    staffToken,
    ...(signal ? { signal } : {}),
  })
}

export function postOutcomes(
  staffToken: string,
  visitId: string,
  body: RecordOutcomesRequest,
): Promise<unknown> {
  return request<unknown>(routes.adminOutcomes(visitId), { method: 'POST', body, staffToken })
}

export function fetchEnquiries(staffToken: string, signal?: AbortSignal): Promise<EnquiryListResponse> {
  return request<EnquiryListResponse>(routes.adminEnquiries(), {
    staffToken,
    ...(signal ? { signal } : {}),
  })
}

/**
 * The refusals matter more than the success here, and they arrive as
 * `ApiRequestError` with the API's own code — the page shows the message
 * rather than deciding for itself what went wrong.
 */
export function linkEnquiry(
  staffToken: string,
  sessionId: string,
  body: LinkEnquiryRequest,
): Promise<LinkEnquiryResponse> {
  return request<LinkEnquiryResponse>(routes.adminLinkEnquiry(sessionId), { method: 'POST', body, staffToken })
}

export function fetchCompleteness(staffToken: string, signal?: AbortSignal): Promise<CompletenessResponse> {
  return request<CompletenessResponse>(routes.adminCompleteness(), {
    staffToken,
    ...(signal ? { signal } : {}),
  })
}

export function fetchCatalogue(
  staffToken: string,
  params: { limit?: number; offset?: number } = {},
  signal?: AbortSignal,
): Promise<CatalogueListResponse> {
  return request<CatalogueListResponse>(routes.adminCatalogue(), {
    query: { ...params },
    staffToken,
    ...(signal ? { signal } : {}),
  })
}

export function fetchRack(staffToken: string, signal?: AbortSignal): Promise<RackListResponse> {
  return request<RackListResponse>(routes.adminRack(), { staffToken, ...(signal ? { signal } : {}) })
}

export function setGarmentStatus(
  staffToken: string,
  garmentId: string,
  body: SetGarmentStatusRequest,
): Promise<GarmentResponse> {
  return request<GarmentResponse>(routes.adminGarmentStatus(garmentId), { method: 'POST', body, staffToken })
}

export function setRackItemStatus(
  staffToken: string,
  itemId: string,
  body: SetRackItemStatusRequest,
): Promise<RackItemResponse> {
  return request<RackItemResponse>(routes.adminRackItemStatus(itemId), { method: 'POST', body, staffToken })
}

/* ------------------------------- sync gowns ------------------------------ */

/**
 * Reads Airtable and answers with what a sync would do. Writes nothing.
 *
 * POST for a read, matching the route: it spends someone else's rate limit, so
 * it must not be something a prefetch or a retry can fire on its own.
 */
export function scanGownSync(staffToken: string, signal?: AbortSignal): Promise<SyncScan> {
  return request<SyncScan>(routes.adminSyncScan(), { method: 'POST', staffToken, ...(signal ? { signal } : {}) })
}

/**
 * Starts the write and returns at once — the run is minutes long and is
 * watched through `fetchGownSyncRun`, not waited on here. `scanId` names the
 * table the stylist read; the server re-reads and refuses if it has moved.
 */
export function applyGownSync(staffToken: string, body: ApplySyncRequest): Promise<SyncRunResponse> {
  return request<SyncRunResponse>(routes.adminSyncApply(), { method: 'POST', body, staffToken })
}

/** The run in flight, or the last one there was. `null` when there has been none. */
export function fetchGownSyncRun(staffToken: string, signal?: AbortSignal): Promise<SyncRunResponse> {
  return request<SyncRunResponse>(routes.adminSyncRun(), { staffToken, ...(signal ? { signal } : {}) })
}

/* --------------------------- words from Airtable -------------------------- */

/** Every wording the sync knows — built-in and the boutique's — with the options to rule onto. */
export function fetchSyncWords(staffToken: string, signal?: AbortSignal): Promise<UpstreamVocabularyResponse> {
  return request<UpstreamVocabularyResponse>(routes.adminSyncWords(), { staffToken, ...(signal ? { signal } : {}) })
}

/** Same as one of ours, or ignore. Replaces any earlier ruling on the wording. */
export function setSyncWord(staffToken: string, body: SetUpstreamWordRequest): Promise<UpstreamVocabularyResponse> {
  return request<UpstreamVocabularyResponse>(routes.adminSyncWords(), { method: 'PUT', body, staffToken })
}

/** A new option on the attribute, with the wording mapped onto it. */
export function addSyncWordOption(staffToken: string, body: AddUpstreamOptionRequest): Promise<UpstreamVocabularyResponse> {
  return request<UpstreamVocabularyResponse>(routes.adminSyncWordsNewOption(), { method: 'POST', body, staffToken })
}

/** Forget the boutique's ruling; a built-in one, if any, applies again. */
export function removeSyncWord(staffToken: string, body: RemoveUpstreamWordRequest): Promise<UpstreamVocabularyResponse> {
  return request<UpstreamVocabularyResponse>(routes.adminSyncWordsRemove(), { method: 'POST', body, staffToken })
}

/* ------------------------------ the storefront --------------------------- */

/**
 * Reads the catalogue and the storefront and answers with what publishing
 * would do. Writes nothing.
 *
 * POST for a read, matching the route: it spends the same cost budget the
 * publish itself comes out of, so it must not be something a prefetch or a
 * retry can fire on its own.
 */
export function planStorefront(staffToken: string, signal?: AbortSignal): Promise<StorefrontPlan> {
  return request<StorefrontPlan>(routes.adminStorefrontPlan(), {
    method: 'POST',
    staffToken,
    ...(signal ? { signal } : {}),
  })
}

/**
 * Starts the publish and returns at once. `planId` names the table the owner
 * read; the server re-reads both sides and refuses if either has moved.
 */
export function publishStorefront(
  staffToken: string,
  body: PublishStorefrontRequest,
): Promise<StorefrontRunResponse> {
  return request<StorefrontRunResponse>(routes.adminStorefrontPublish(), { method: 'POST', body, staffToken })
}

/**
 * The publish in flight, or the last one there was - and whether this
 * deployment has a storefront connected at all. `configured: false` is a real
 * answer, not an error.
 */
export function fetchStorefrontRun(staffToken: string, signal?: AbortSignal): Promise<StorefrontRunResponse> {
  return request<StorefrontRunResponse>(routes.adminStorefrontRun(), {
    staffToken,
    ...(signal ? { signal } : {}),
  })
}

/* ---------------------------- the theme workspace ------------------------ */

/** Which files a workspace covers, the latest version of each, and whether there is a theme at all. */
export function fetchThemeWorkspace(staffToken: string, signal?: AbortSignal): Promise<ThemeWorkspaceStatus> {
  return request<ThemeWorkspaceStatus>(routes.adminThemeWorkspace(), { staffToken, ...(signal ? { signal } : {}) })
}

/** Mints a key. The token in the answer is shown once and never again. */
export function openThemeWorkspace(staffToken: string): Promise<ThemeWorkspaceGrant> {
  return request<ThemeWorkspaceGrant>(routes.adminThemeWorkspace(), { method: 'POST', staffToken })
}

export function fetchThemeHistory(staffToken: string, path?: string, signal?: AbortSignal): Promise<ThemeHistoryResponse> {
  return request<ThemeHistoryResponse>(routes.adminThemeHistory(path), { staffToken, ...(signal ? { signal } : {}) })
}

/* ----------------------------- the public door --------------------------- */

/**
 * Is there a door for someone who has not booked?
 *
 * `false` is a 404, which is every deployment that has not asked for one, and
 * the caller renders nothing at all — the same discovery the demo board uses,
 * for the same reason: the client is told what exists by asking, not by a build
 * flag it would have to be rebuilt to change. GET creates nothing; only the
 * POST below mints a session, which is why the question has its own verb.
 */
export async function isPublicStartOpen(signal?: AbortSignal): Promise<boolean> {
  try {
    await request<unknown>(routes.start(), { ...(signal ? { signal } : {}) })
    return true
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 404) return false
    throw error
  }
}

/** Mints a session with no appointment behind it, and returns its link once. */
export function startFitting(signal?: AbortSignal): Promise<StartSessionResponse> {
  return request<StartSessionResponse>(routes.start(), { method: 'POST', ...(signal ? { signal } : {}) })
}

/* ------------------------- booking and sharing ------------------------- */

/** What `/reserve` offers; `s` is a fitting room's token when it came from one. */
export function fetchReserveOptions(sessionToken: string | undefined, signal?: AbortSignal): Promise<ReserveOptionsResponse> {
  return request<ReserveOptionsResponse>(routes.reserve(), { query: { s: sessionToken }, ...(signal ? { signal } : {}) })
}

/** The website's way in for a type that takes a card: the agreement alone, and Stripe's page next. */
export function reserveHold(body: ReserveHoldRequest): Promise<ReserveResponse> {
  return request<ReserveResponse>(routes.reserveHold(), { method: 'POST', body })
}

export function reserve(body: ReserveRequest): Promise<ReserveResponse> {
  return request<ReserveResponse>(routes.reserve(), { method: 'POST', body })
}

export function confirmReserve(intentId: string): Promise<ReserveConfirmResponse> {
  return request<ReserveConfirmResponse>(routes.reserveConfirm(intentId), { method: 'POST' })
}

/** Back from Koalendar: by the booking id when its redirect passed one, else by the cookie this browser left with. */
export function fetchBooked(bookingId: string | undefined, signal?: AbortSignal): Promise<BookedResponse> {
  return request<BookedResponse>(routes.booked(bookingId), signal ? { signal } : {})
}

export function shareFittingRoom(token: string): Promise<ShareLinkResponse> {
  return request<ShareLinkResponse>(routes.sessionShare(token), { method: 'POST' })
}

export function fetchSharedRoom(shareToken: string, signal?: AbortSignal): Promise<SharedRoomResponse> {
  return request<SharedRoomResponse>(routes.shared(shareToken), signal ? { signal } : {})
}

export function fetchVisitCard(staffToken: string, visitId: string, signal?: AbortSignal): Promise<VisitCardResponse> {
  return request<VisitCardResponse>(routes.adminVisitCard(visitId), { staffToken, ...(signal ? { signal } : {}) })
}

export function chargeNoShowFee(staffToken: string, visitId: string): Promise<Pick<VisitCardResponse, 'card'>> {
  return request<Pick<VisitCardResponse, 'card'>>(routes.adminVisitNoShowFee(visitId), { method: 'POST', staffToken })
}

/* ------------------------------- signing in ------------------------------ */

/**
 * What the sign-in page needs before it can draw itself.
 *
 * Asked rather than compiled in, for the same reason `isPublicStartOpen` is
 * asked: a client that learns what its deployment offers from a build flag has
 * to be rebuilt to change it, and the console and the API are two containers.
 */
export function fetchAuthConfig(signal?: AbortSignal): Promise<AuthConfigResponse> {
  return request<AuthConfigResponse>(routes.authConfig(), { ...(signal ? { signal } : {}) })
}

/** Google's ID token in, this deployment's session out. */
export function signInWithGoogle(credential: string): Promise<StaffSessionResponse> {
  return request<StaffSessionResponse>(routes.authGoogle(), {
    method: 'POST',
    body: { credential } satisfies GoogleSignInRequest,
  })
}

/**
 * Who the API says this credential is.
 *
 * The console asks on load rather than trusting what it stored: a session that
 * was minted as `owner` and whose address has since come off the roster is
 * refused by the server on the next request anyway, and finding that out from
 * the answer to this question is better than finding out from a visit list that
 * will not load.
 */
export function fetchStaffIdentity(
  staffToken: string,
  signal?: AbortSignal,
): Promise<{ identity: StaffIdentity; via: 'shared-token' | 'google' }> {
  return request(routes.authMe(), { staffToken, ...(signal ? { signal } : {}) })
}

/* --------------------------------- demo ---------------------------------- */

/**
 * Demo scaffolding, deliberately not in `@fitting-room/contracts`: the shape is
 * declared here so nothing outside this file can grow a dependency on a surface
 * that is off in every deployment that matters. The API answers 404 when
 * `FITTING_ROOM_DEMO_LINKS` is not set, and `undefined` is how the caller is
 * told to render nothing at all.
 */
export interface DemoLink {
  customerName: string
  tierKey: string
  activity: string
  /** Relative, so the link is right on whatever origin the page was served from. */
  path: string
}

export interface DemoLinks {
  links: DemoLink[]
  staffToken: string | null
}

export async function fetchDemoLinks(signal?: AbortSignal): Promise<DemoLinks | undefined> {
  try {
    return await request<DemoLinks>(routes.demoLinks(), { ...(signal ? { signal } : {}) })
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 404) return undefined
    throw error
  }
}
